import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircleIcon, TruckIcon, ClockIcon, CalendarIcon, ShoppingBagIcon, CreditCardIcon, MapPinIcon } from '@heroicons/react/24/solid';
import TopBar from '../../global/TopBar';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';

const OrderConfirmation = () => {
  const location = useLocation();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = useSelector((state) => state.auth.token);
  useEffect(() => {
    const fetchOrderDetails = async () => {
      const searchParams = new URLSearchParams(location.search);
      const orderId = searchParams.get('order_id');

      if (!orderId) {
        setError('No order ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_STORE_API_URL}/get-order/${orderId}`,
          {
            headers: {
              "Content-Type": "application/json",
              "Api-Name": "store-api",
              "Authorization": `Bearer ${token}`
            }
          }
        );
        if (!response.ok) {
          throw new Error('Failed to fetch order details');
        }
        const data = await response.json();
        setOrderDetails(data.order);
        // Trigger confetti animation
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [location]);

  const statusSteps = [
    { name: 'Order Placed', icon: CheckCircleIcon, current: true },
    { name: 'Processing', icon: ClockIcon, current: orderDetails?.status === 'Processing' },
    { name: 'Shipped', icon: TruckIcon, current: orderDetails?.status === 'Shipped' },
    { name: 'Delivered', icon: CalendarIcon, current: orderDetails?.status === 'Delivered' },
  ];

  const renderOrderSummary = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.8 }}
      className="bg-sec-dark/50 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden"
    >
      <div className="p-6 border-b border-pri-gray/10">
        <h2 className="text-xl font-bold flex items-center">
          <ShoppingBagIcon className="h-6 w-6 mr-2 text-pri-yellow" />
          Order Summary
        </h2>
      </div>

      <div className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full mb-6">
            <thead>
              <tr className="border-b border-pri-gray/10">
                <th className="text-left py-3 text-pri-gray font-medium">Item</th>
                <th className="text-center py-3 text-pri-gray font-medium">Qty</th>
                <th className="text-right py-3 text-pri-gray font-medium">Price</th>
                <th className="text-right py-3 text-pri-gray font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {orderDetails.items.map((item, index) => (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="group"
                  >
                    <td className="py-4">
                      <div className="flex items-center space-x-3">
                        <div className="relative w-12 h-12 rounded-lg overflow-hidden group-hover:scale-105 transition-transform duration-300">
                          <img 
                            src={item.image} 
                            alt={item.name} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-pri-white font-medium">{item.name}</span>
                      </div>
                    </td>
                    <td className="text-center py-4 text-pri-white">{item.quantity}</td>
                    <td className="text-right py-4 text-pri-white">£{item.price.toFixed(2)}</td>
                    <td className="text-right py-4 text-pri-yellow font-semibold">
                      £{(item.price * item.quantity).toFixed(2)}
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
          className="space-y-3"
        >
          <div className="flex justify-between text-sm text-pri-gray">
            <span>Subtotal</span>
            <span className="text-pri-white">£{orderDetails.subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm text-pri-gray">
            <span>Tax</span>
            <span className="text-pri-white">£{orderDetails.tax.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm text-pri-gray">
            <span>Shipping</span>
            <span className="text-pri-green">Free</span>
          </div>
          <motion.div
            className="flex justify-between pt-4 border-t border-pri-gray/10"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3, delay: 1.2 }}
          >
            <span className="text-lg font-bold text-pri-white">Total</span>
            <span className="text-lg font-bold text-pri-yellow">£{orderDetails.total.toFixed(2)}</span>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );

  const renderOrderStatus = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.6 }}
      className="bg-sec-dark/50 backdrop-blur-sm rounded-xl p-8 shadow-lg relative overflow-hidden"
    >
      <h2 className="text-xl font-bold mb-8 flex items-center">
        <ClockIcon className="h-6 w-6 mr-2 text-pri-yellow" />
        Order Status
      </h2>

      <div className="relative">
        {/* Progress Bar Background */}
        <div className="absolute top-1/2 left-0 w-full h-1 bg-pri-gray/20 transform -translate-y-1/2 rounded-full" />

        {/* Animated Progress Bar */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: statusSteps.filter(step => step.current).length / statusSteps.length }}
          transition={{ delay: 1.2, duration: 0.8, ease: "easeOut" }}
          className="absolute top-1/2 left-0 h-1 bg-gradient-to-r from-pri-yellow to-pri-green transform -translate-y-1/2 origin-left rounded-full"
          style={{ width: '100%' }}
        />

        {/* Status Steps */}
        <div className="relative z-10 flex justify-between">
          {statusSteps.map((step, index) => (
            <div key={step.name} className="flex flex-col items-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.8 + index * 0.2, type: "spring", stiffness: 500, damping: 30 }}
                className={`rounded-full p-3 ${
                  step.current 
                    ? 'bg-gradient-to-r from-pri-yellow to-pri-green shadow-lg' 
                    : 'bg-pri-gray/20'
                }`}
              >
                <step.icon className={`h-6 w-6 ${
                  step.current ? 'text-pri-dark' : 'text-pri-gray'
                }`} />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 + index * 0.2, duration: 0.3 }}
                className="mt-3"
              >
                <p className={`text-sm font-medium ${
                  step.current ? 'text-pri-yellow' : 'text-pri-gray'
                }`}>
                  {step.name}
                </p>
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );

  if (loading) return (
    <div className="min-h-screen bg-pri-dark flex items-center justify-center">
      <motion.div
        className="w-16 h-16 border-t-4 border-pri-yellow rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
  
  if (error) return (
    <div className="min-h-screen bg-pri-dark text-pri-white flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-sec-dark p-8 rounded-lg shadow-lg"
      >
        <h2 className="text-2xl font-bold mb-4">Error</h2>
        <p>{error}</p>
      </motion.div>
    </div>
  );
  
  if (!orderDetails) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pri-dark to-sec-dark text-pri-white">
      <TopBar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Success Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-sec-dark/50 backdrop-blur-sm rounded-xl p-8 shadow-lg mb-8 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 20, delay: 0.2 }}
              className="w-20 h-20 mx-auto bg-gradient-to-r from-pri-yellow to-pri-green rounded-full flex items-center justify-center mb-6"
            >
              <CheckCircleIcon className="h-12 w-12 text-pri-dark" />
            </motion.div>
            
            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-pri-yellow to-pri-green text-transparent bg-clip-text mb-4">
              Order Confirmed!
            </h1>
            <p className="text-pri-gray text-lg">
              Thank you for your purchase. Your order has been received and is being processed.
            </p>
          </motion.div>

          {/* Order Details Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8"
          >
            {[
              { icon: ShoppingBagIcon, title: "Order Number", value: orderDetails.id },
              { icon: CalendarIcon, title: "Order Date", value: new Date(orderDetails.date).toLocaleDateString() },
              { icon: CreditCardIcon, title: "Total Amount", value: `£${orderDetails.total.toFixed(2)}` },
              { icon: MapPinIcon, title: "Delivery Address", value: orderDetails.address }
            ].map((detail, index) => (
              <motion.div
                key={detail.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="bg-sec-dark/50 backdrop-blur-sm p-4 rounded-xl"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-pri-dark/30 rounded-lg">
                    <detail.icon className="h-6 w-6 text-pri-yellow" />
                  </div>
                  <div>
                    <p className="text-pri-gray text-sm">{detail.title}</p>
                    <p className="text-pri-white font-medium">{detail.value}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {renderOrderStatus()}
          {renderOrderSummary()}

          {/* Continue Shopping Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.2 }}
            className="text-center mt-8"
          >
            <Link
              to={`/store/${orderDetails.category}`}
              className="inline-block bg-gradient-to-r from-pri-yellow to-pri-green text-pri-dark px-8 py-3 rounded-xl font-semibold hover:opacity-90 transition-all duration-300 shadow-lg"
            >
              Continue Shopping
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
